import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, List, Plus, Settings2 } from 'lucide-react';
import { PageHeader, ErrorState } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, Skeleton } from '@/components/ui/misc';
import { formatMoney } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useBoard, useMoveDeal, usePipelines } from './api';
import { DEAL_TYPE_LABELS } from './labels';
import { DealDialog } from './deal-dialog';
import { MoveDealDialog } from './move-deal-dialog';
import type { BoardColumn, Deal } from './types';

/**
 * The pipeline Kanban (spec PIPE-03). Cards are dragged between columns with
 * the HTML drag-and-drop API — no library, and it keeps keyboard users on the
 * card menu, which does the same thing.
 */
export function DealsBoardPage() {
  const { data: pipelines = [] } = usePipelines();
  const [pipelineId, setPipelineId] = useState<string>('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [creating, setCreating] = useState(false);
  const [dragging, setDragging] = useState<Deal | null>(null);
  const [pendingMove, setPendingMove] = useState<{ deal: Deal; column: BoardColumn } | null>(null);

  const { data, isLoading, error, refetch } = useBoard({
    pipelineId: pipelineId || undefined,
    ownerId: ownerFilter || undefined,
  });
  const move = useMoveDeal();

  const currency = data?.columns[0]?.deals[0]?.currency ?? 'AED';

  const onDrop = (column: BoardColumn) => {
    if (!dragging || dragging.stageId === column.stage.id) {
      setDragging(null);
      return;
    }

    // Won and lost need extra input, and a stage may demand fields first — both
    // go through the dialog rather than failing silently on the board.
    const needsDialog =
      column.stage.stageType !== 'OPEN' ||
      missingFields(dragging, findStage(data?.columns, dragging.stageId)).length > 0;

    if (needsDialog) {
      setPendingMove({ deal: dragging, column });
    } else {
      move.mutate({ id: dragging.id, stageId: column.stage.id });
    }
    setDragging(null);
  };

  return (
    <div>
      <PageHeader
        title="Deal pipeline"
        description="Drag a deal to move it. Won and lost ask for the details the spec requires."
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/crm/deals/list">
                <List className="h-4 w-4" />
                List view
              </Link>
            </Button>
            <Can permission="crm.deal.create">
              <Button onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4" />
                New deal
              </Button>
            </Can>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select
          value={pipelineId}
          onChange={(event) => setPipelineId(event.target.value)}
          className="h-9 w-auto min-w-[12rem]"
        >
          <option value="">Default pipeline</option>
          {pipelines.map((pipeline) => (
            <option key={pipeline.id} value={pipeline.id}>
              {pipeline.name}
              {pipeline.isDefault ? ' (default)' : ''}
            </option>
          ))}
        </Select>
        <Can permission="crm.pipeline.create">
          <Button asChild variant="ghost" size="sm">
            <Link to="/crm/pipelines/deal">
              <Settings2 className="h-4 w-4" />
              Deal pipeline setup
            </Link>
          </Button>
        </Can>
        <Select
          value={ownerFilter}
          onChange={(event) => setOwnerFilter(event.target.value)}
          className="h-9 w-auto min-w-[10rem]"
        >
          <option value="">Everyone</option>
          {ownerOptions(data?.columns).map((owner) => (
            <option key={owner.id} value={owner.id}>
              {owner.name}
            </option>
          ))}
        </Select>

        {data && (
          <div className="ml-auto text-sm text-muted-foreground">
            <span className="font-medium text-foreground">
              {formatMoney(sumColumns(data.columns, 'totalValue'), currency)}
            </span>{' '}
            open ·{' '}
            <span className="font-medium text-foreground">
              {formatMoney(sumColumns(data.columns, 'weightedValue'), currency)}
            </span>{' '}
            weighted
          </div>
        )}
      </div>

      {error ? (
        <ErrorState message="Could not load the pipeline." onRetry={() => void refetch()} />
      ) : isLoading ? (
        <div className="flex gap-3 overflow-x-auto">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-96 w-[calc(100%-2.5rem)] shrink-0 md:w-72" />
          ))}
        </div>
      ) : (
        <div className="scrollbar-thin flex snap-x snap-mandatory gap-3 overflow-x-auto pb-4 md:snap-none">
          {data?.columns.map((column) => (
            <div
              key={column.stage.id}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => onDrop(column)}
              className={cn(
                // One stage per swipe on a phone: the column fills the scroller except
                // for a sliver of the next, and the scroll snaps to it. On a desktop
                // the columns are narrow again and snapping would fight the drag.
                'flex w-[calc(100%-2.5rem)] shrink-0 snap-start flex-col rounded-lg border bg-muted/30 transition-colors md:w-72',
                dragging && dragging.stageId !== column.stage.id && 'border-primary/40 bg-primary/5',
              )}
            >
              <div className="border-b px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: column.stage.colour }}
                  />
                  <span className="truncate text-sm font-medium">{column.stage.name}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{column.count}</span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {formatMoney(column.totalValue, currency)}
                  {column.stage.stageType === 'OPEN' && (
                    <span className="ml-1">
                      · {formatMoney(column.weightedValue, currency)} weighted
                    </span>
                  )}
                </div>
              </div>

              <div className="scrollbar-thin flex-1 space-y-2 overflow-y-auto p-2">
                {column.deals.map((deal) => (
                  <DealCard
                    key={deal.id}
                    deal={deal}
                    currency={currency}
                    onDragStart={() => setDragging(deal)}
                    onDragEnd={() => setDragging(null)}
                  />
                ))}
                {column.deals.length === 0 && (
                  <p className="px-2 py-6 text-center text-xs text-muted-foreground">
                    Nothing here
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <DealDialog pipelineId={pipelineId || undefined} onClose={() => setCreating(false)} />
      )}
      {pendingMove && (
        <MoveDealDialog
          deal={pendingMove.deal}
          targetStage={pendingMove.column.stage}
          currentStage={findStage(data?.columns, pendingMove.deal.stageId)}
          onClose={() => setPendingMove(null)}
        />
      )}
    </div>
  );
}

function DealCard({
  deal,
  currency,
  onDragStart,
  onDragEnd,
}: {
  deal: Deal;
  currency: string;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  return (
    <Link
      to={`/crm/deals/${deal.id}`}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className="block cursor-grab rounded-md border bg-background p-2.5 shadow-sm transition-shadow hover:shadow-md active:cursor-grabbing"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="line-clamp-2 text-sm font-medium">{deal.title}</span>
        {deal.isRotting && (
          <span title="No activity for a while">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
          </span>
        )}
      </div>

      <div className="mt-1 truncate text-xs text-muted-foreground">{deal.company.name}</div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-sm font-semibold tabular">{formatMoney(deal.value, currency)}</span>
        <Badge variant="secondary" className="shrink-0 text-[10px]">
          {DEAL_TYPE_LABELS[deal.dealType]}
        </Badge>
      </div>

      <div className="mt-1.5 flex items-center justify-between text-[11px] text-muted-foreground">
        <span className="truncate">{deal.owner?.name ?? 'Unassigned'}</span>
        <span className="shrink-0">{deal.number}</span>
      </div>
    </Link>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function findStage(columns: BoardColumn[] | undefined, stageId: string) {
  return columns?.find((column) => column.stage.id === stageId)?.stage;
}

/** Which of the current stage's required fields the deal has not filled in. */
export function missingFields(
  deal: Deal,
  stage: { requiredFields: string[] } | undefined,
): string[] {
  if (!stage) return [];
  return stage.requiredFields.filter((field) => {
    switch (field) {
      case 'value':
        return !deal.value || deal.value === '0.00';
      case 'expectedCloseDate':
        return !deal.expectedCloseDate;
      case 'contactId':
        return !deal.contactId;
      case 'ownerId':
        return !deal.ownerId;
      case 'probability':
        return deal.probability === null;
      case 'source':
        return !deal.source;
      default:
        return false;
    }
  });
}

function sumColumns(columns: BoardColumn[], key: 'totalValue' | 'weightedValue'): string {
  // Values are decimal strings; sum in minor units so nothing is lost.
  const total = columns.reduce((sum, column) => {
    const value = column[key] ?? '0';
    const [whole = '0', fraction = '00'] = value.replace('-', '').split('.');
    const minor = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0').slice(0, 2));
    return sum + (value.startsWith('-') ? -minor : minor);
  }, 0n);

  const negative = total < 0n;
  const abs = negative ? -total : total;
  return `${negative ? '-' : ''}${abs / 100n}.${(abs % 100n).toString().padStart(2, '0')}`;
}

function ownerOptions(columns: BoardColumn[] | undefined) {
  const owners = new Map<string, { id: string; name: string }>();
  for (const column of columns ?? []) {
    for (const deal of column.deals) {
      if (deal.owner) owners.set(deal.owner.id, { id: deal.owner.id, name: deal.owner.name });
    }
  }
  return [...owners.values()].sort((a, b) => a.name.localeCompare(b.name));
}
